import { Link, useSearchParams } from "react-router-dom";
import {
  Building2,
  Loader2,
  ChevronRight,
  ArrowRight,
  Compass,
  CheckCircle2,
  Clock,
  Circle,
} from "lucide-react";

export const inr = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export const startLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export function Logo({ light = false, className = "" }) {
  return (
    <Link to="/" className={`flex items-center gap-2.5 ${className}`} data-testid="brand-logo">
      <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm border border-black/5 shrink-0">
        <img src="/logo-icon.png" alt="MySocietyMitra" className="w-8 h-8 object-contain" />
      </div>
      <div className="leading-tight">
        <span className={`font-display font-bold text-lg tracking-tight block ${light ? "text-white" : "text-foreground"}`}>
          MySociety<span className="text-[#2E6BE6]">Mitra</span>
        </span>
        <span className={`text-[10px] ${light ? "text-white/60" : "text-muted-foreground"}`}>by Shivkrupa Enterprises</span>
      </div>
    </Link>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-foreground">{title}</h1>
        {subtitle && <p className="text-muted-foreground mt-1 text-sm md:text-base">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}

const SOCIETY_HEADER_TONES = {
  blue: "bg-[#2563EB]/10 text-[#2563EB]",
  green: "bg-emerald-100 text-emerald-600",
  rose: "bg-rose-100 text-rose-500",
  amber: "bg-amber-100 text-amber-600",
  violet: "bg-violet-100 text-violet-600",
  teal: "bg-teal-100 text-teal-600",
  orange: "bg-orange-100 text-orange-600",
};

// Premium Society Admin page header (scoped to /app pages only). Keeps the plain
// PageHeader above unchanged for the other portals.
export function SocietyPageHeader({ title, subtitle, icon: Icon, tone = "blue", children }) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${SOCIETY_HEADER_TONES[tone] || SOCIETY_HEADER_TONES.blue}`}>
            <Icon className="h-6 w-6" />
          </div>
        )}
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-[#0F2B63] md:text-3xl">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-slate-500 md:text-base">{subtitle}</p>}
        </div>
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}


export function StatCard({ icon: Icon, label, value, tone = "primary", testid }) {
  const tones = {
    primary: "bg-primary/10 text-primary",
    secondary: "bg-secondary/10 text-secondary",
    amber: "bg-amber-100 text-amber-700",
    rose: "bg-rose-100 text-rose-700",
  };
  return (
    <div className="bg-card rounded-xl border p-5 shadow-[0_8px_32px_rgba(0,0,0,0.04)]" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tones[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div className="mt-4 font-display text-2xl font-bold tracking-tight">{value}</div>
      <div className="text-sm text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

export function Loading() {
  return (
    <div className="flex items-center justify-center py-24">
      <Loader2 className="w-7 h-7 animate-spin text-primary" />
    </div>
  );
}

export function EmptyState({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="text-center py-16 bg-card rounded-xl border">
      {Icon && <Icon className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />}
      <h3 className="font-display font-semibold text-lg">{title}</h3>
      {subtitle && <p className="text-muted-foreground text-sm mt-1 max-w-md mx-auto">{subtitle}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function DemoBadge() {
  return (
    <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold px-2 py-0.5">
      DEMO DATA
    </span>
  );
}

// Keeps a Tabs component's active tab in the URL (?tab=) so other pages
// can deep-link to a specific workflow step. Falls back to defaultTab.
export function useTabParam(defaultTab, key = "tab") {
  const [params, setParams] = useSearchParams();
  const tab = params.get(key) || defaultTab;
  const setTab = (value) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    setParams(next, { replace: true });
  };
  return [tab, setTab];
}

// Compact breadcrumb trail. items: [{ label, to? }]; last item is current.
export function Breadcrumbs({ items }) {
  return (
    <nav
      aria-label="Breadcrumb"
      className="mb-3 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground"
      data-testid="breadcrumbs"
    >
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        return (
          <span className="flex items-center gap-1.5" key={item.label}>
            {index > 0 && <ChevronRight className="h-3 w-3 shrink-0" />}
            {item.to && !isLast ? (
              <Link
                className="transition-colors hover:text-foreground"
                data-testid={`breadcrumb-link-${index}`}
                to={item.to}
              >
                {item.label}
              </Link>
            ) : (
              <span className="font-medium text-foreground" data-testid="breadcrumb-current">
                {item.label}
              </span>
            )}
          </span>
        );
      })}
    </nav>
  );
}

// Guided "next steps" strip. steps: [{ label, to, icon?, show?, testid, status? }]
// status (optional): "completed" | "in_progress" | "pending" renders a badge.
const WORKFLOW_STATUS = {
  completed: { label: "Completed", icon: CheckCircle2, className: "text-emerald-600" },
  in_progress: { label: "In progress", icon: Clock, className: "text-amber-500" },
  pending: { label: "Pending", icon: Circle, className: "text-muted-foreground" },
};

export function WorkflowGuide({ steps, title = "Guided next steps" }) {
  const visible = (steps || []).filter((step) => step.show !== false);
  if (!visible.length) return null;
  return (
    <section
      className="mt-6 rounded-xl border bg-card p-4"
      data-testid="workflow-guide"
    >
      <div className="mb-3 flex items-center gap-2">
        <Compass className="h-4 w-4 text-primary" />
        <h2 className="font-display text-sm font-semibold">{title}</h2>
      </div>
      <div className="flex flex-wrap gap-2">
        {visible.map((step) => {
          const Icon = step.icon;
          const status = step.status ? WORKFLOW_STATUS[step.status] : null;
          const StatusIcon = status?.icon;
          return (
            <Link
              className="group flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors hover:border-primary hover:bg-primary/5"
              data-testid={step.testid}
              key={`${step.to}-${step.label}`}
              to={step.to}
            >
              {Icon && <Icon className="h-4 w-4 text-primary" />}
              <span className="font-medium">{step.label}</span>
              {status ? (
                <span
                  className={`flex items-center gap-1 text-xs font-medium ${status.className}`}
                  data-testid={`${step.testid}-status`}
                  data-status={step.status}
                >
                  <StatusIcon className="h-3.5 w-3.5" />
                  {status.label}
                </span>
              ) : (
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
              )}
            </Link>
          );
        })}
      </div>
    </section>
  );
}
